import api from './api';
import {
  IAgentBuild,
  ICreateLicenseDto,
  ILicense,
  ILicenseFilters,
  IRelease,
} from 'interfaces/license.interface';
import { IPageableResponse } from 'interfaces/shared.interface';

export const getLicenses = (
  page: number,
  size: number,
  filters?: Partial<ILicenseFilters>,
): Promise<IPageableResponse<ILicense>> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });

  // Blank filters are omitted rather than sent empty: the backend treats a missing
  // parameter and an empty one the same way, and leaving them out keeps the URL
  // readable when someone is looking at the network tab.
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  return api.get(`licenses?${params.toString()}`);
};

export const createLicense = (data: ICreateLicenseDto): Promise<ILicense> => {
  return api.post('licenses', data);
};

export const getLicenseById = (id: number): Promise<ILicense> => {
  return api.get(`licenses/${id}`);
};

export const downloadLicense = (id: number): Promise<Blob> => {
  return api.get(`licenses/${id}/download`, { responseType: 'blob' }).then((res: any) => res.data);
};

export const revokeLicense = (id: number): Promise<ILicense> => {
  return api.post(`licenses/${id}/revoke`);
};

export const deleteLicense = (id: number): Promise<void> => {
  return api.delete(`licenses/${id}`);
};

// ---------------------------------------------------------------- agent installers

export const requestAgentBuild = (
  licenseId: number,
  backendUrl?: string,
  extensionAutoInstall?: boolean,
): Promise<IAgentBuild> => {
  // Sending the URL also stores it on the licence, so a rebuild after the next release
  // needs no re-typing.
  const body: Record<string, unknown> = {};
  if (backendUrl) body.backendUrl = backendUrl;
  // Sent as a string because the endpoint reads a flat string map. Omitted entirely when
  // undefined, which the server records as "no preference" - the agent then keeps its own
  // default rather than being explicitly switched either way.
  if (extensionAutoInstall !== undefined) {
    body.extensionAutoInstall = String(extensionAutoInstall);
  }
  return api.post(`licenses/${licenseId}/agent`, body);
};

/**
 * Both downloads are large - a stamped installer is ~157 MB and the server zip ~91 MB -
 * and the API sends Content-Length for each, so real progress is available rather than
 * just a spinner. `onProgress` receives 0-100, or null when the length is unknown.
 */
export type DownloadProgress = (percent: number | null) => void;

const progressConfig = (onProgress?: DownloadProgress) =>
  onProgress
    ? {
        onDownloadProgress: (e: any) => {
          onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : null);
        },
      }
    : {};

export const downloadAgent = (licenseId: number, onProgress?: DownloadProgress): Promise<Blob> => {
  return api
    .get(`licenses/${licenseId}/agent/download`, {
      responseType: 'blob',
      ...progressConfig(onProgress),
    })
    .then((res: any) => res.data);
};

// ---------------------------------------------------------------- release

export const getCurrentRelease = (): Promise<IRelease> => {
  return api.get('releases/current');
};

export const downloadReleaseZip = (onProgress?: DownloadProgress): Promise<Blob> => {
  return api
    .get('releases/current/zip', { responseType: 'blob', ...progressConfig(onProgress) })
    .then((res: any) => res.data);
};

export const downloadReleaseExtension = (onProgress?: DownloadProgress): Promise<Blob> => {
  return api
    .get('releases/current/extension', { responseType: 'blob', ...progressConfig(onProgress) })
    .then((res: any) => res.data);
};

// All versions' notes up to the current release, as one PDF - the same file the zip carries.
export const downloadReleaseNotes = (): Promise<Blob> => {
  return api
    .get('releases/current/release-notes', { responseType: 'blob' })
    .then((res: any) => res.data);
};
